import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Alert, Badge, Modal, Form } from 'react-bootstrap';
import { useAuth } from '../context/AuthContext';
import { collection, query, where, getDocs, doc, updateDoc, getDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { FaCheck, FaTimes, FaEye, FaEdit } from 'react-icons/fa';

const VerifikasiAkun = () => {
  const { currentUser, userRole } = useAuth();
  const [pendingUsers, setPendingUsers] = useState([]);
  const [verifiedUsers, setVerifiedUsers] = useState([]);
  const [rejectedUsers, setRejectedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [activeTab, setActiveTab] = useState('pending');

  useEffect(() => {
    fetchUsers();
  }, [currentUser, userRole]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Ambil pengguna yang belum diverifikasi dan belum ditolak
      const pendingQuery = query(
        collection(db, "users"),
        where("verified", "==", false)
      );
      const pendingSnapshot = await getDocs(pendingQuery);
      
      const pendingData = [];
      const rejectedData = [];
      
      pendingSnapshot.forEach(doc => {
        const userData = doc.data();
        // Pisahkan pengguna yang ditolak dan yang menunggu verifikasi
        if (userData.rejected) {
          rejectedData.push({
            id: doc.id,
            ...userData
          });
        } else {
          pendingData.push({
            id: doc.id,
            ...userData
          });
        }
      });
      
      setPendingUsers(pendingData);
      setRejectedUsers(rejectedData);
      
      // Ambil pengguna yang sudah diverifikasi
      const verifiedQuery = query(
        collection(db, "users"),
        where("verified", "==", true)
      );
      const verifiedSnapshot = await getDocs(verifiedQuery);
      
      const verifiedData = [];
      verifiedSnapshot.forEach(doc => {
        verifiedData.push({
          id: doc.id,
          ...doc.data()
        });
      });
      
      setVerifiedUsers(verifiedData);
      setError('');
    } catch (error) {
      console.error("Error fetching users:", error);
      setError("Terjadi kesalahan saat mengambil data");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (userId) => {
    try {
      const userRef = doc(db, "users", userId);
      
      // Dapatkan data pengguna terlebih dahulu untuk memeriksa apakah ditolak
      const userDoc = await getDoc(userRef);
      const userData = userDoc.exists() ? userDoc.data() : null;
      
      // Perbarui data pengguna
      const updateData = {
        verified: true
      };
      
      // Jika pengguna sebelumnya ditolak, hapus status penolakan
      if (userData && userData.rejected) {
        updateData.rejected = false;
        updateData.rejectionReason = null;
        updateData.rejectedAt = null;
      }
      
      await updateDoc(userRef, updateData);
      
      setSuccess("Akun berhasil diverifikasi");
      
      // Refresh data
      fetchUsers();
      
      // Tutup modal jika terbuka
      if (showDetailModal) {
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error("Error verifying user:", error);
      setError("Terjadi kesalahan saat memverifikasi akun");
    }
  };

  const handleReject = async (userId) => {
    if (!window.confirm('Yakin ingin menolak akun ini?')) return;
    
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        verified: false,
        rejected: true,
        rejectionReason: "Akun ditolak oleh Penanggung Jawab Tugas Akhir",
        rejectedAt: new Date().toISOString()
      });
      
      setSuccess("Akun berhasil ditolak");
      
      // Refresh data
      fetchUsers();
      
      // Tutup modal jika terbuka
      if (showDetailModal) {
        setShowDetailModal(false);
      }
    } catch (error) {
      console.error("Error rejecting user:", error);
      setError("Terjadi kesalahan saat menolak akun");
    }
  };
  
  // Handle restore rejected user
  const handleRestoreUser = async (userId) => {
    if (!window.confirm('Yakin ingin mengembalikan akun ini ke status menunggu verifikasi?')) return;
    
    try {
      const userRef = doc(db, "users", userId);
      await updateDoc(userRef, {
        rejected: false,
        rejectionReason: null,
        rejectedAt: null
      });
      
      setSuccess("Akun berhasil dikembalikan ke status menunggu verifikasi");
      
      // Refresh data
      fetchUsers();
    } catch (error) {
      console.error("Error restoring user:", error);
      setError("Terjadi kesalahan saat mengembalikan status akun");
    }
  };

  // Handle delete user
  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Yakin ingin menghapus akun ini? Tindakan ini tidak dapat dibatalkan.')) return;
    
    try {
      const userRef = doc(db, "users", userId);
      await deleteDoc(userRef);
      setSuccess("Akun berhasil dihapus");
      fetchUsers();
    } catch (error) {
      console.error("Error deleting user:", error);
      setError("Terjadi kesalahan saat menghapus akun");
    }
  };

  const handleShowDetail = (user) => {
    setSelectedUser(user);
    setShowDetailModal(true);
  };
  
  const handleShowEdit = (user) => {
    setEditUser({...user});
    setShowEditModal(true);
  };
  
  const handleUpdateUser = async () => {
    try {
      const userRef = doc(db, "users", editUser.id);
      
      // Mengumpulkan data yang akan diupdate
      const updateData = {
        nama: editUser.nama
      };
      
      // Tambahkan field sesuai dengan role
      if (editUser.role === 'mahasiswa') {
        updateData.nim = editUser.nim;
        updateData.prodi = editUser.prodi;
      } else {
        updateData.nip = editUser.nip;
        updateData.fakultas = editUser.fakultas;
        updateData.jurusan = editUser.jurusan;
      }
      
      await updateDoc(userRef, updateData);
      
      setSuccess("Data pengguna berhasil diperbarui");
      setShowEditModal(false);
      
      // Refresh data
      fetchUsers();
    } catch (error) {
      console.error("Error updating user:", error);
      setError("Terjadi kesalahan saat memperbarui data pengguna");
    }
  };

  const renderUserTable = (users, isPending = true) => {
    if (users.length === 0) {
      return (
        <Alert variant="info">
          {isPending 
            ? "Tidak ada akun yang menunggu verifikasi" 
            : "Belum ada akun yang diverifikasi"}
        </Alert>
      );
    }

    return (
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>No</th>
            <th>Nama</th>
            <th>Email</th>
            <th>Peran</th>
            <th>NIM/NIP</th>
            <th>Tanggal Daftar</th>
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {users.map((user, index) => (
            <tr key={user.id}>
              <td>{index + 1}</td>
              <td>{user.nama}</td>
              <td>{user.email}</td>
              <td>
                <Badge bg={user.role === 'mahasiswa' ? 'info' : 'secondary'}>
                  {user.role === 'mahasiswa' ? 'Mahasiswa' : 'Dosen'}
                </Badge>
              </td>
              <td>{user.role === 'mahasiswa' ? user.nim : user.nip}</td>
              <td>{user.createdAt ? new Date(user.createdAt).toLocaleDateString() : '-'}</td>
              <td>
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-info" 
                    size="sm"
                    onClick={() => handleShowDetail(user)}
                  >
                    <FaEye />
                  </Button>
                  
                  {isPending ? (
                    <>
                      <Button 
                        variant="outline-success" 
                        size="sm"
                        onClick={() => handleVerify(user.id)}
                        title="Verifikasi Akun"
                      >
                        <FaCheck />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleReject(user.id)}
                        title="Tolak Akun"
                      >
                        <FaTimes />
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button 
                        variant="outline-warning" 
                        size="sm"
                        onClick={() => handleShowEdit(user)}
                        title="Edit Akun"
                      >
                        <FaEdit />
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteUser(user.id)}
                        title="Hapus Akun"
                        className="ms-2"
                      >
                        <FaTimes />
                      </Button>
                    </>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const renderRejectedUserTable = () => {
    if (rejectedUsers.length === 0) {
      return (
        <Alert variant="info">
          Tidak ada akun yang ditolak
        </Alert>
      );
    }

    return (
      <Table responsive striped bordered hover>
        <thead>
          <tr>
            <th>No</th>
            <th>Nama</th>
            <th>Email</th>
            <th>Peran</th>
            <th>NIM/NIP</th>
            <th>Tanggal Ditolak</th>
            <th>Aksi</th>
          </tr>
        </thead>
        <tbody>
          {rejectedUsers.map((user, index) => (
            <tr key={user.id}>
              <td>{index + 1}</td>
              <td>{user.nama}</td>
              <td>{user.email}</td>
              <td>
                <Badge bg={user.role === 'mahasiswa' ? 'info' : 'secondary'}>
                  {user.role === 'mahasiswa' ? 'Mahasiswa' : 'Dosen'}
                </Badge>
              </td>
              <td>{user.role === 'mahasiswa' ? user.nim : user.nip}</td>
              <td>{user.rejectedAt ? new Date(user.rejectedAt).toLocaleDateString() : '-'}</td>
              <td>
                <div className="d-flex gap-2">
                  <Button 
                    variant="outline-info" 
                    size="sm"
                    onClick={() => handleShowDetail(user)}
                  >
                    <FaEye />
                  </Button>
                  <Button 
                    variant="outline-success" 
                    size="sm"
                    onClick={() => handleVerify(user.id)}
                    title="Verifikasi Akun"
                  >
                    <FaCheck />
                  </Button>
                  <Button 
                    variant="outline-warning" 
                    size="sm"
                    onClick={() => handleRestoreUser(user.id)}
                    title="Kembalikan ke Menunggu Verifikasi"
                  >
                    <FaEdit />
                  </Button>
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => handleDeleteUser(user.id)}
                    title="Hapus Akun"
                  >
                    <FaTimes />
                  </Button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  };

  const renderUserDetail = () => {
    if (!selectedUser) return null;

    return (
      <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Detail Pengguna</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nama Lengkap</Form.Label>
              <Form.Control type="text" value={selectedUser.nama} readOnly />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Email</Form.Label>
              <Form.Control type="email" value={selectedUser.email} readOnly />
            </Form.Group>
            
            <Form.Group className="mb-3">
              <Form.Label>Peran</Form.Label>
              <Form.Control 
                type="text" 
                value={selectedUser.role === 'mahasiswa' ? 'Mahasiswa' : 'Dosen'} 
                readOnly 
              />
            </Form.Group>
            
            {selectedUser.role === 'mahasiswa' ? (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>NIM</Form.Label>
                  <Form.Control type="text" value={selectedUser.nim} readOnly />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Program Studi</Form.Label>
                  <Form.Control type="text" value={selectedUser.prodi} readOnly />
                </Form.Group>
              </>
            ) : (
              <>
                <Form.Group className="mb-3">
                  <Form.Label>NIP</Form.Label>
                  <Form.Control type="text" value={selectedUser.nip} readOnly />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Fakultas</Form.Label>
                  <Form.Control type="text" value={selectedUser.fakultas} readOnly />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Jurusan</Form.Label>
                  <Form.Control type="text" value={selectedUser.jurusan} readOnly />
                </Form.Group>
              </>
            )}
            
            <Form.Group className="mb-3">
              <Form.Label>Tanggal Pendaftaran</Form.Label>
              <Form.Control 
                type="text" 
                value={selectedUser.createdAt ? new Date(selectedUser.createdAt).toLocaleString() : '-'} 
                readOnly 
              />
            </Form.Group>
            
            {selectedUser.rejected && (
              <>
                <hr />
                <h5 className="text-danger">Informasi Penolakan</h5>
                
                <Form.Group className="mb-3">
                  <Form.Label>Alasan Penolakan</Form.Label>
                  <Form.Control 
                    as="textarea" 
                    rows={2}
                    value={selectedUser.rejectionReason || 'Tidak ada alasan yang diberikan'} 
                    readOnly 
                  />
                </Form.Group>
                
                <Form.Group className="mb-3">
                  <Form.Label>Tanggal Ditolak</Form.Label>
                  <Form.Control 
                    type="text" 
                    value={selectedUser.rejectedAt ? new Date(selectedUser.rejectedAt).toLocaleString() : '-'} 
                    readOnly 
                  />
                </Form.Group>
              </>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
            Tutup
          </Button>
          
          {!selectedUser.verified && !selectedUser.rejected && (
            <>
              <Button variant="success" onClick={() => handleVerify(selectedUser.id)}>
                Verifikasi
              </Button>
              <Button variant="danger" onClick={() => handleReject(selectedUser.id)}>
                Tolak
              </Button>
            </>
          )}
          
          {selectedUser.rejected && (
            <>
              <Button variant="success" onClick={() => handleVerify(selectedUser.id)}>
                Verifikasi
              </Button>
              <Button variant="warning" onClick={() => handleRestoreUser(selectedUser.id)}>
                Kembalikan ke Menunggu Verifikasi
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    );
  };

  if (loading) {
    return (
      <Container className="py-4">
        <Alert variant="info">Memuat data pengguna...</Alert>
      </Container>
    );
  }

  return (
    <Container className="py-4">
      <Card>
        <Card.Body>
          <Card.Title>Verifikasi Akun</Card.Title>
          
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}
          
          <div className="mb-3">
            <Button 
              variant={activeTab === 'pending' ? 'primary' : 'outline-primary'} 
              className="me-2"
              onClick={() => setActiveTab('pending')}
            >
              Menunggu Verifikasi ({pendingUsers.length})
            </Button>
            <Button 
              variant={activeTab === 'verified' ? 'primary' : 'outline-primary'} 
              className="me-2"
              onClick={() => setActiveTab('verified')}
            >
              Sudah Diverifikasi ({verifiedUsers.length})
            </Button>
            <Button 
              variant={activeTab === 'rejected' ? 'primary' : 'outline-primary'} 
              onClick={() => setActiveTab('rejected')}
            >
              Ditolak ({rejectedUsers.length})
            </Button>
          </div>
          
          {activeTab === 'pending' ? (
            renderUserTable(pendingUsers, true)
          ) : activeTab === 'verified' ? (
            renderUserTable(verifiedUsers, false)
          ) : (
            renderRejectedUserTable()
          )}
          
          {renderUserDetail()}
          
          {/* Edit Modal */}
          <Modal show={showEditModal} onHide={() => setShowEditModal(false)} size="lg">
            <Modal.Header closeButton>
              <Modal.Title>Edit Pengguna</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              {editUser && (
                <Form>
                  <Form.Group className="mb-3">
                    <Form.Label>Nama Lengkap</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editUser.nama || ''} 
                      onChange={(e) => setEditUser({...editUser, nama: e.target.value})}
                    />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Email</Form.Label>
                    <Form.Control type="email" value={editUser.email || ''} readOnly />
                  </Form.Group>
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Peran</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editUser.role === 'mahasiswa' ? 'Mahasiswa' : 'Dosen'} 
                      readOnly 
                    />
                  </Form.Group>
                  
                  {editUser.role === 'mahasiswa' ? (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>NIM</Form.Label>
                        <Form.Control 
                          type="text" 
                          value={editUser.nim || ''} 
                          onChange={(e) => setEditUser({...editUser, nim: e.target.value})}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Program Studi</Form.Label>
                        <Form.Control 
                          type="text" 
                          value={editUser.prodi || ''} 
                          onChange={(e) => setEditUser({...editUser, prodi: e.target.value})}
                        />
                      </Form.Group>
                    </>
                  ) : (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label>NIP</Form.Label>
                        <Form.Control 
                          type="text" 
                          value={editUser.nip || ''} 
                          onChange={(e) => setEditUser({...editUser, nip: e.target.value})}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Fakultas</Form.Label>
                        <Form.Control 
                          type="text" 
                          value={editUser.fakultas || ''} 
                          onChange={(e) => setEditUser({...editUser, fakultas: e.target.value})}
                        />
                      </Form.Group>
                      
                      <Form.Group className="mb-3">
                        <Form.Label>Jurusan</Form.Label>
                        <Form.Control 
                          type="text" 
                          value={editUser.jurusan || ''} 
                          onChange={(e) => setEditUser({...editUser, jurusan: e.target.value})}
                        />
                      </Form.Group>
                    </>
                  )}
                  
                  <Form.Group className="mb-3">
                    <Form.Label>Tanggal Pendaftaran</Form.Label>
                    <Form.Control 
                      type="text" 
                      value={editUser.createdAt ? new Date(editUser.createdAt).toLocaleString() : '-'} 
                      readOnly 
                    />
                  </Form.Group>
                </Form>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowEditModal(false)}>
                Batal
              </Button>
              <Button variant="primary" onClick={handleUpdateUser}>
                Simpan Perubahan
              </Button>
            </Modal.Footer>
          </Modal>
        </Card.Body>
      </Card>
    </Container>
  );
};

export default VerifikasiAkun;
